/** Route guard for Studio (INSTRUCTOR+). */
import { createContext, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthProvider';
import LoadingIcon from '@/foundation/ui/LoadingIcon';

// ─── Ready signal context ────────────────────────────────────────────────────
// Kept for StudioEntry compatibility — no-op now that the ceremony is removed.

interface StudioGateContextValue {
  signalReady: () => void;
}

const StudioGateContext = createContext<StudioGateContextValue>({
  signalReady: () => {},
});

export function useStudioGateReady() {
  return useContext(StudioGateContext);
}

// ─── Guard ───────────────────────────────────────────────────────────────────

interface RequireStudioProps {
  children: React.ReactNode;
}

export function RequireStudio({ children }: RequireStudioProps) {
  const { loading: authLoading, isInstructor } = useAuth();

  if (authLoading) {
    return (
      <div
        className="flex min-h-dvh items-start justify-center pt-[28vh]"
        style={{ background: 'var(--color-bg)' }}
      >
        <LoadingIcon size="lg" />
      </div>
    );
  }

  if (!isInstructor) {
    return <Navigate to="/" replace />;
  }

  return (
    <StudioGateContext.Provider value={{ signalReady: () => {} }}>
      {children}
    </StudioGateContext.Provider>
  );
}
